import ExcelJS from "exceljs";
import { formatDateVN } from "../../../utils/formatUtils";
import { workDayToApiParam } from "../../../utils/workDayApiParam";

const thinBorder = {
  top: { style: "thin", color: { argb: "FFCCCCCC" } },
  left: { style: "thin", color: { argb: "FFCCCCCC" } },
  bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
  right: { style: "thin", color: { argb: "FFCCCCCC" } },
};

const statusLabelVi = (status) => {
  const s = (status || "").toLowerCase();
  switch (s) {
    case "waiting":
      return "Chờ duyệt";
    case "approved":
      return "Đã duyệt";
    case "reported":
      return "Đã báo cáo";
    case "completed":
      return "Hoàn thành";
    case "rejected":
      return "Không duyệt";
    case "rejected-report":
      return "Chưa đạt";
    default:
      return status || "";
  }
};

const latestReportContent = (task) => {
  const reps = task?.reports;
  if (!Array.isArray(reps) || reps.length === 0) return "";
  const sorted = [...reps].sort(
    (a, b) => new Date(b.reported_at || 0) - new Date(a.reported_at || 0)
  );
  return sorted[0]?.report_content || "";
};

const tagsText = (task) => {
  const tags = task?.tags;
  if (!Array.isArray(tags) || tags.length === 0) return "";
  return tags
    .map((t) => t.tag_name)
    .filter(Boolean)
    .join(", ");
};

/** Ngày làm việc theo lịch VN — tránh lệch khi BE trả ISO UTC (slice 10 ký tự đầu lấy nhầm ngày UTC). */
const formatWorkDayCell = (workDay) => {
  if (workDay == null || workDay === "") return "";
  const normalized = workDayToApiParam(workDay);
  if (normalized) return normalized;
  const vn = formatDateVN(workDay);
  return vn === "-" ? "" : vn;
};

/**
 * @param {object[]} items — hàng từ API GET /hrm/tasks (không summary_mode)
 * @param {{ from_date: string, to_date: string, filename: string }} opts
 */
export async function downloadTasksExcel(items, opts) {
  const { from_date, to_date, filename } = opts;
  if (!Array.isArray(items) || items.length === 0) return;

  const headers = [
    "STT",
    "Ngày làm việc",
    "Nhân viên",
    "Chi nhánh",
    "Bộ phận",
    "Tên công việc",
    "Loại",
    "Ghi chú",
    "Trạng thái",
    "Nội dung báo cáo (mới nhất)",
    "Điểm",
    "Phản hồi đánh giá",
    "Người đánh giá",
    "Thẻ",
  ];

  const title = `Danh sách công việc — từ ${from_date || "?"} đến ${to_date || "?"}\nTổng ${items.length} dòng`;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("CongViec", {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  const numCols = headers.length;
  ws.mergeCells(1, 1, 1, numCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 13 };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getRow(1).height = 40;

  const headerRow = ws.getRow(2);
  headers.forEach((val, idx) => {
    const c = headerRow.getCell(idx + 1);
    c.value = val;
    c.font = { bold: true, size: 11 };
    c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    c.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" },
    };
    c.border = thinBorder;
  });
  headerRow.height = 22;

  items.forEach((task, i) => {
    const r = ws.getRow(3 + i);
    r.height = 20;
    const row = [
      i + 1,
      formatWorkDayCell(task.work_day),
      task.employee_name || "",
      task.branch_name || "",
      task.department_name || "",
      task.taskname || "",
      task.task_type || "",
      task.note || "",
      statusLabelVi(task.status),
      latestReportContent(task),
      task.judge_point != null && task.judge_point !== "" ? task.judge_point : "",
      task.judge_content || "",
      task.judged_by_name || "",
      tagsText(task),
    ];
    row.forEach((val, colIndex) => {
      const c = r.getCell(colIndex + 1);
      c.value = val;
      c.border = thinBorder;
      if (colIndex === 0) {
        c.alignment = { horizontal: "center", vertical: "middle" };
      } else if ([5, 7, 9, 11, 12, 13].includes(colIndex)) {
        c.alignment = { horizontal: "left", vertical: "top", wrapText: true };
      } else {
        c.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
      }
    });
  });

  ws.getColumn(1).width = 5;
  ws.getColumn(2).width = 14;
  ws.getColumn(3).width = 24;
  ws.getColumn(4).width = 18;
  ws.getColumn(5).width = 18;
  ws.getColumn(6).width = 36;
  ws.getColumn(7).width = 12;
  ws.getColumn(8).width = 22;
  ws.getColumn(9).width = 14;
  ws.getColumn(10).width = 40;
  ws.getColumn(11).width = 8;
  ws.getColumn(12).width = 28;
  ws.getColumn(13).width = 18;
  ws.getColumn(14).width = 24;

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Kết xuất Excel tổng hợp công việc theo nhân viên.
 * @param {object[]} items — từ GET /hrm/tasks/export-summary
 * @param {{ from_date: string, to_date: string, filename: string }} opts
 */
export async function downloadTasksSummaryExcel(items, opts) {
  const { from_date, to_date, filename } = opts;
  if (!Array.isArray(items) || items.length === 0) return;

  const headers = [
    "STT",
    "Chi nhánh",
    "Nhân viên",
    "Số lượng công việc đăng ký",
    "Số lượng công việc được giao",
    "Số lượng công việc đã báo cáo",
    "Số lượng công việc đã báo cáo / Số ngày đã chấm công",
    "Đánh giá",
  ];

  const title = `Tổng hợp công việc — từ ${from_date || "?"} đến ${to_date || "?"}\nTổng ${items.length} nhân viên`;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("TongHopCongViec", {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  const numCols = headers.length;
  ws.mergeCells(1, 1, 1, numCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 13 };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getRow(1).height = 40;

  const headerRow = ws.getRow(2);
  headers.forEach((val, idx) => {
    const c = headerRow.getCell(idx + 1);
    c.value = val;
    c.font = { bold: true, size: 11 };
    c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    c.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" },
    };
    c.border = thinBorder;
  });
  headerRow.height = 32;

  items.forEach((row, i) => {
    const registered = Number(row.registered_count) || 0;
    const assigned = Number(row.assigned_count) || 0;
    const reported = Number(row.reported_count) || 0;
    const attendanceDays = Number(row.attendance_days) || 0;
    const missingDays = Number(row.missing_task_days) || 0;
    const ratioText = `${reported} / ${attendanceDays}`;
    const evaluation =
      row.evaluation ||
      (missingDays === 0 ? "Đủ báo cáo" : `Thiếu báo cáo (${missingDays} ngày)`);

    const r = ws.getRow(3 + i);
    r.height = 20;
    const values = [
      i + 1,
      row.branch_name || "",
      row.employee_name || "",
      registered,
      assigned,
      reported,
      ratioText,
      evaluation,
    ];
    values.forEach((val, colIndex) => {
      const c = r.getCell(colIndex + 1);
      c.value = val;
      c.border = thinBorder;
      if (colIndex === 0 || (colIndex >= 3 && colIndex <= 6)) {
        c.alignment = { horizontal: "center", vertical: "middle" };
      } else if (colIndex === 7) {
        c.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
        if (missingDays > 0) {
          c.font = { color: { argb: "FFB91C1C" }, bold: true };
        } else {
          c.font = { color: { argb: "FF15803D" } };
        }
      } else {
        c.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
      }
    });
  });

  ws.getColumn(1).width = 5;
  ws.getColumn(2).width = 22;
  ws.getColumn(3).width = 28;
  ws.getColumn(4).width = 18;
  ws.getColumn(5).width = 18;
  ws.getColumn(6).width = 18;
  ws.getColumn(7).width = 42;
  ws.getColumn(8).width = 28;

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
